import type { UnitSystem } from "../../../types";
import type { ConnectorFamilyForSettings } from "./connectorFamilies";
import type { ConnectorForSettings } from "./connectors";
import type { FasteningTypeForSettings } from "./fasteningTypes";
import type { GroovingTypeForSettings } from "./groovingTypes";
import type { SecondaryItem } from "./secondaryItems";
import type { ScrewForSettings } from "./screws";
import type { SubstructureMaterialClassForSettings } from "./substructureMaterialClasses";
import type { SubstructureMaterialForSettings } from "./substructureMaterials";
import type { SubstructureForSettings } from "./substructures";
import type { SupportFamilyForSettings } from "./supportFamilies";
import type { SupportForSettings } from "./supports";

export const RECTANGULAR_SHAPE = "rectangular";
export const L_SHAPE = "l";
export const DIFFERENT_BOARD_POSITION_START = "start";
export const DIFFERENT_BOARD_POSITION_END = "end";

export type ConfigurationShape = typeof RECTANGULAR_SHAPE | typeof L_SHAPE;
export type DifferentBoardPosition = typeof DIFFERENT_BOARD_POSITION_START | typeof DIFFERENT_BOARD_POSITION_END;

export type OutlineVertex = {
  index: number;
  xMm: number;
  yMm: number;
};

export type OutlineEdge = {
  from: number;
  to: number;
};

export type Outline = {
  version: number;
  vertices: OutlineVertex[];
  edges: OutlineEdge[];
};

// A terrain slope drawn as an arrow from start to end, towards where the terrain rises, in the
// outline's frame (mm, same origin as its vertices - it can lie outside the deck).
export type Slope = {
  slopeDegrees: number;
  startXMm: number;
  startYMm: number;
  endXMm: number;
  endYMm: number;
};

// Saved in the calculation data. Its settings entities come (and are sent back) localized, as in the
// settings response.
export type Configuration = {
  id: string;
  name: string;
  // display only: every measurement is saved in metric units whatever the unit system
  unitSystem: UnitSystem;
  liveLoad: number | null;
  deflection: number | null;
  shape: ConfigurationShape | null;
  // null until the deck is drawn - see deckOutline.ts for the convention.
  outline: Outline | null;
  totalHeight: number | null;
  // Empty = flat terrain. At most one slope for now.
  slopes: Slope[];
  hasHorizontalBoards: boolean | null;
  fasteningType: FasteningTypeForSettings | null;
  groovingType: GroovingTypeForSettings | null;
  gap: number | null;
  connectorFamily: ConnectorFamilyForSettings | null;
  connector: ConnectorForSettings | null;
  connectorSubstructureScrew: ScrewForSettings | null;
  connectorBoardScrew: ScrewForSettings | null;
  boardWidth: number | null;
  longDifferentBoardWidth: number | null;
  shortDifferentBoardWidth: number | null;
  longDifferentBoardPosition: DifferentBoardPosition | null;
  shortDifferentBoardPosition: DifferentBoardPosition | null;
  boardThickness: number | null;
  groovingHeight: number | null;
  substructureMaterial: SubstructureMaterialForSettings | null;
  substructureMaterialClass: SubstructureMaterialClassForSettings | null;
  substructure: SubstructureForSettings | null;
  joistsSpacing: number | null;
  secondaryItem: SecondaryItem | null;
  supportFamily: SupportFamilyForSettings | null;
  support: SupportForSettings | null;
  supportScrew: ScrewForSettings | null;
};
